import moment from "moment";
import { ReturnExchangesApi } from "../../../../apis/ReturnExchangesApi";
import { useApiInfoContextValueHook } from "../../../../contexts/ApiInfoContext";
import { SellertoolSignatureUtils } from "../../../../utils/sellertool-signature-utils";
import { useQueryParamsHook } from "../../../../utils/useQueryParamsHook";
import { useReturnExchangesActionsContextHook } from "../contexts/ReturnExchangesContext";
import { ReturnExchangeArchivesApi } from "../../../../apis/ReturnExchangeArchivesApi";
import { useReturnExchangeArchivesActionsContextHook } from "../contexts/ReturnExchangeArchivesContext";
import { useSelectedReturnExchangesActionsContextHook } from "../contexts/SelectedReturnExchangesContext";

export function useFetcher(props) {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const returnExchangesActionsContextHook = useReturnExchangesActionsContextHook();
    const returnExchangeArchivesActionsContextHook = useReturnExchangeArchivesActionsContextHook();
    const selectedReturnExchangesActionsContextHook = useSelectedReturnExchangesActionsContextHook();
    const queryParamsHook = useQueryParamsHook();

    const fetchReturnExchanges = async () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        })

        const headers = {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }

        let params = {}

        // 페이지네이션 조건
        params.page = queryParamsHook?.params?.page ?? 1;
        params.pageSize = queryParamsHook?.params?.pageSize ?? 20;

        // 조회 및 검색어 조건
        if (queryParamsHook?.params?.searchCondition && queryParamsHook?.params?.searchQuery) {
            params.searchCondition = queryParamsHook?.params?.searchCondition;
            params.searchQuery = queryParamsHook?.params?.searchQuery;
        }

        // 날짜 조건
        if (queryParamsHook?.params?.startDate && queryParamsHook?.params?.endDate) {
            // 2024-01-01T13:00:00.000+09:00 형태로
            params.startDateTime = moment(queryParamsHook?.params?.startDate).hour(0).minute(0).second(0).millisecond(0).toISOString();
            params.endDateTime = moment(queryParamsHook?.params?.endDate).hour(23).minute(59).second(59).millisecond(999).toISOString();
        } else {
            // 현재날짜
            params.startDateTime = moment().hour(0).minute(0).second(0).millisecond(0).toISOString();
            params.endDateTime = moment().hour(23).minute(59).second(59).millisecond(999).toISOString();
        }

        // 보관함 조건
        if (queryParamsHook?.params?.archiveId) {
            params.returnExchangeArchiveId = queryParamsHook?.params?.archiveId;
        }

        // 반품/교환 타입 조건
        if (queryParamsHook?.params?.returnExchangeType) {
            params.returnExchangeType = queryParamsHook?.params?.returnExchangeType;
        }

        // 진행상태 조건
        if (queryParamsHook?.params?.returnExchangeProceedStatuses) {
            params.returnExchangeProceedStatuses = queryParamsHook?.params?.returnExchangeProceedStatuses;
        }

        const returnExchangesResponse = await ReturnExchangesApi().searchList({
            params: params,
            headers: headers
        });

        if (returnExchangesResponse?.content) {
            returnExchangesActionsContextHook.returnExchanges.onSet(returnExchangesResponse?.content?.items);

            const totalPage = Math.ceil(returnExchangesResponse?.content?.totalCount / returnExchangesResponse?.content?.pageSize);
            const hasPrev = returnExchangesResponse?.content?.page > 1;
            const nextPage = returnExchangesResponse?.content?.hasNext ? returnExchangesResponse?.content?.page + 1 : returnExchangesResponse?.content?.page;
            const prevPage = hasPrev ? returnExchangesResponse?.content?.page - 1 : 1;

            returnExchangesActionsContextHook.pagination.onSet({
                page: returnExchangesResponse?.content?.page,
                pageSize: returnExchangesResponse?.content?.pageSize,
                hasNext: returnExchangesResponse?.content?.hasNext,
                hasPrev: hasPrev,
                totalCount: returnExchangesResponse?.content?.totalCount,
                totalPage: totalPage,
                nextPage: nextPage,
                prevPage: prevPage
            });

            // 선택된 목록이 있을 경우, 선택된 목록을 업데이트
            selectedReturnExchangesActionsContextHook.selectedReturnExchanges.setValue(prev => {
                return prev.map(item => {
                    const index = returnExchangesResponse?.content?.items.findIndex(r => r._id === item._id);
                    if (index === -1) {
                        return item;
                    }
                    return returnExchangesResponse?.content?.items[index];
                })
            })
        }
    }

    const fetchReturnExchangeArchives = async () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
            secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
            timestamp: timestamp
        })

        const headers = {
            'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }

        const response = await ReturnExchangeArchivesApi().searchList({
            headers: headers
        });

        if (response?.content) {
            returnExchangeArchivesActionsContextHook.returnExchangeArchives.onSet(response?.content);
        }
    }

    return {
        fetchReturnExchanges,
        fetchReturnExchangeArchives
    }
}